/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import hyphenateStyleName from 'hyphenate-style-name';
import * as React from 'react';

import { useEventCallback, useLiveRef, useSafeLayoutEffect } from '@gitbook/util-react-hooks';

import { normalizeStyle, type ViewStyle } from '../web';
import type { AnimateLifecycleProps } from './AnimateLifecycle';
import type { AnimationCallbacks, AnimationDeclaration, AnimationPhases } from './animation';
import { type AnimateInternalManager, AnimateInternalManagerContext } from './context';

enum LifecyclePhase {
    MOUNTING = 'MOUNTING',
    MOUNTED = 'MOUNTED',
    UNMOUNTING = 'UNMOUNTING',
    UNMOUNTED = 'UNMOUNTED',
}

/**
 * Implementation for the browser of the Animate component.
 * Wrap a component to animate it when it is mounted or unmounted.
 */
export function AnimateLifecycle(props: AnimateLifecycleProps): React.ReactElement {
    const { children, onUnmount } = props;
    const hasChildren = React.Children.toArray(children).length > 0;

    const notifyUnmount = useEventCallback(() => {
        onUnmount?.();
    });
    const [descendants] = React.useState(
        () =>
            new Map<
                React.Ref<HTMLElement>,
                { phases: AnimationPhases; element: HTMLElement; callbacks: AnimationCallbacks }
            >()
    );
    const bindAnimations = useEventCallback(
        (
            ref: React.Ref<HTMLElement>,
            phases: AnimationPhases,
            callbacks: AnimationCallbacks
        ): React.RefCallback<HTMLElement> => {
            return (element) => {
                if (element) {
                    descendants.set(ref, {
                        phases,
                        callbacks,
                        // @ts-ignore we know it's the web platform
                        element: element as HTMLElement,
                    });
                } else {
                    descendants.delete(ref);
                }
            };
        }
    );
    const manager = React.useMemo<AnimateInternalManager>(
        () => ({ bindAnimations }),
        [bindAnimations]
    );

    /**
     * Start an animation and returns a callback to cancel it.
     */
    const animate = useEventCallback(
        (animationPhase: 'mount' | 'unmount', onFinished: () => void) => {
            // Count of animations to wait for.
            let pending = 0;

            /** Callback when the animation is finished for all elements */
            const onAnimationEnd = () => {
                onFinished();
            };

            if (descendants.size === 0) {
                onAnimationEnd();
                return () => {};
            }

            const cleanups = Array.from(descendants.entries()).map(
                ([_ref, { phases, element, callbacks }]) => {
                    // @ts-ignore we know we are using RNW
                    const animation = phases[animationPhase];

                    if (!element || !animation) {
                        return;
                    }

                    const onElementAnimationEnd = () => {
                        if (animationPhase === 'unmount') {
                            // Hide element to emulate the unmount until we remove it from the DOM
                            // We use opacity instead of display:none to ensure it doesn't cancel
                            // other potential animations.
                            element.style.visibility = 'hidden';
                        }

                        pending -= 1;
                        if (pending === 0) {
                            onAnimationEnd();
                        }

                        callbacks.onEnd?.();
                    };

                    /** Callbak when one of the element finished animating */
                    const onElementAnimationEndEvent = (event: Event) => {
                        const element = event.currentTarget as HTMLElement;

                        if (event && element !== event.target) {
                            // If the event was generated for something NOT this element we
                            // should ignore it as it's not relevant to us
                            return;
                        }

                        onElementAnimationEnd();
                    };

                    pending += 1;

                    // Start the animation
                    let elementAnimation: Animation | undefined;
                    const frameId = window.requestAnimationFrame(() => {
                        elementAnimation = animateElement(element, animation);

                        // Listen to the animation finished - it might also be removed or cancelled depending
                        // on the state of the DOM.
                        elementAnimation.addEventListener('finish', onElementAnimationEndEvent);
                        elementAnimation.addEventListener('remove', (e) => {
                            onElementAnimationEndEvent(e);
                        });
                        elementAnimation.addEventListener('cancel', (e) => {
                            onElementAnimationEndEvent(e);
                        });
                    });

                    return () => {
                        window.cancelAnimationFrame(frameId);
                        elementAnimation?.cancel();
                        onElementAnimationEnd();
                    };
                }
            );

            return () => {
                for (const cleanup of cleanups) {
                    cleanup?.();
                }
            };
        }
    );

    const [phase, setPhase] = React.useState(
        hasChildren ? LifecyclePhase.MOUNTING : LifecyclePhase.UNMOUNTED
    );

    const phaseRef = useLiveRef(phase);

    //
    // Transition to the next phase when the component is rendered as mounted/unmounted
    //
    useSafeLayoutEffect(() => {
        if (hasChildren) {
            setPhase(LifecyclePhase.MOUNTING);
        } else if (phaseRef.current !== LifecyclePhase.UNMOUNTED) {
            setPhase(LifecyclePhase.UNMOUNTING);
        }
    }, [hasChildren, phaseRef]);

    //
    // Animate in a second effect to ensure the component has been rendered with the right phase
    // and the refs are ready
    //
    useSafeLayoutEffect(() => {
        let cancelled = false;
        let stopAnimation: null | (() => void) = null;

        if (phase === LifecyclePhase.MOUNTING) {
            stopAnimation = animate('mount', () => {
                if (!cancelled) {
                    setPhase(LifecyclePhase.MOUNTED);
                }
            });
        } else if (phase === LifecyclePhase.UNMOUNTING) {
            stopAnimation = animate('unmount', () => {
                if (!cancelled) {
                    setPhase(LifecyclePhase.UNMOUNTED);
                    notifyUnmount();
                }
            });
        }

        return () => {
            cancelled = true;
            if (stopAnimation) {
                stopAnimation();
            }
        };
    }, [phase, animate, notifyUnmount]);

    // Keep track of the latest element rendered to continue rendering it during the unmount phase.
    const latestChildrenRef = React.useRef<typeof children>(null);
    useSafeLayoutEffect(() => {
        if (children) {
            latestChildrenRef.current = React.cloneElement(children);
        }
    }, [children]);

    // While unmounting, we keep rendering the previous children
    const render = () => {
        if (children) {
            return children;
        }

        if (phase === LifecyclePhase.UNMOUNTING || phase === LifecyclePhase.MOUNTING) {
            return latestChildrenRef.current;
        }

        return null;
    };

    return (
        <AnimateInternalManagerContext.Provider value={manager}>
            {render()}
        </AnimateInternalManagerContext.Provider>
    );
}

/**
 * Animate an element and return the animation.
 */
export function animateElement(element: HTMLElement, animation: AnimationDeclaration): Animation {
    const keyframes = convertToKeyframes(animation);

    return element.animate(keyframes, {
        duration: animation.duration,
        delay: animation.delay || 0,
        easing: animation.easing || 'cubic-bezier(.05, .69, .14, 1)',
    });
}

/**
 * Convert an animation declaration into an array of keyframes.
 */
function convertToKeyframes(animation: AnimationDeclaration): Keyframe[] {
    return Object.keys(animation.frames)
        .sort() // ensure the frames are sorted
        .map((key) => {
            const frame = animation.frames[Number(key)];
            return convertKeyframe(frame);
        });
}

/**
 * Convert a ReactNative style (defined with a ViewStyle) into a Keyframe.
 *
 * Convert style properties to CSS keyframe properties while keeping values as structured data.
 */
function convertKeyframe(value: ViewStyle): Keyframe {
    const style = normalizeStyle(value);
    const keyframe: Record<string, string | number> = {};
    for (const [key, value] of Object.entries(style ?? {})) {
        keyframe[hyphenateStyleName(key)] = value as string | number;
    }

    return keyframe;
}
